import React, { createContext, useContext, useState, useEffect, PropsWithChildren } from "react";
import axios from "axios";
import { InvoicePatchPayload } from "../components/table/InvoiceList";

export interface AccountingInvoice {
  id: number;
  invoiceNumber: string;
  invoiceDate: Date | undefined;
  sellerName: string;
  sellerPostalCode: string;
  sellerCity: string;
  sellerStreet: string;
  buyerName: string;
  buyerPostalCode: string;
  buyerCity: string;
  buyerStreet: string;
  grossAmount: number;
  netAmount: number;
  currencyCode: string;
  dueDate: Date | undefined;
  paymentReference: string;
  paymentMethod: string;
  iban: string;
  bic: string;
  vatAmount: number;
  vatRate: number;
  orderDate: Date | undefined;
  paymentDate: Date | undefined;
  paymentReason: PaymentReason | null;
  financingPlanItem: FinancingPlanItem | null;
  kostenstelle: Kostenstelle | null;
  createdAt: Date | undefined;
  updatedAt: Date | undefined;
}

interface Kostenstelle {
  id: number;
  name: string;
}

interface PaymentReason {
  id: number;
  reason: string;
  invoices: AccountingInvoice[];
}

interface FinancingPlanItem {
  id: number;
  item: string;
  invoices: AccountingInvoice[];
}

interface InvoiceContextType {
  invoices: AccountingInvoice[];
  fetchInvoices: (kostenstelle?: string) => Promise<void>;
  uploadInvoice: (file: File) => Promise<void>;
  createInvoice: (id: number, data: AccountingInvoice) => Promise<void>;
  updateInvoice: (id: number, data: InvoicePatchPayload) => Promise<void>;
  deleteInvoice: (id: number) => Promise<void>;
}

const InvoiceContext = createContext<InvoiceContextType>({} as InvoiceContextType);

export const InvoiceProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [invoices, setInvoices] = useState<AccountingInvoice[]>([]);

  const token = localStorage.getItem("authToken");

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get("/api/accounting/invoices", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const filtered = response.data.filter((inv: any) => !!inv.id && !!inv.invoiceNumber);
      const mapped = filtered.map((inv: any) => ({
        ...inv,
        invoiceDate: inv.invoiceDate ? new Date(inv.invoiceDate) : null,
        dueDate: inv.dueDate ? new Date(inv.dueDate) : null,
        orderDate: inv.orderDate ? new Date(inv.orderDate) : null,
        paymentDate: inv.paymentDate ? new Date(inv.paymentDate) : null,
        createdAt: inv.createdAt ? new Date(inv.createdAt) : null,
        updatedAt: inv.updatedAt ? new Date(inv.updatedAt) : null,
      }));
      setInvoices(mapped);
    } catch (error) {
      console.error("Fehler beim Abrufen der Rechnungen:", error);
    }
  };

  const uploadInvoice = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post("/api/accounting/upload-invoice", formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      await fetchInvoices();
    } catch (error) {
      console.error("Fehler beim Upload der Rechnung:", error);
    }
  };

  const createInvoice = async (id: number, data: AccountingInvoice) => {
    try {
      await axios.patch(`/api/accounting/invoice/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchInvoices();
    } catch (error) {
      console.error("Fehler beim Erstellen der Rechnung:", error);
    }
  };

  const updateInvoice = async (id: number, data: InvoicePatchPayload) => {
    try {
      await axios.patch(`/api/accounting/invoice/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchInvoices();
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Rechnung:", error);
    }
  };

  const deleteInvoice = async (id: number) => {
    try {
      await axios.delete(`/api/accounting/invoice/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchInvoices();
    } catch (error) {
      console.error("Fehler beim Löschen der Rechnung:", error);
    }
  };

  return (
    <InvoiceContext.Provider
      value={{ invoices, fetchInvoices, uploadInvoice, createInvoice, updateInvoice, deleteInvoice }}>
      {children}
    </InvoiceContext.Provider>
  );
};

export const useInvoices = () => useContext(InvoiceContext);
